import { useCallback, useSyncExternalStore } from "react"
import { validate as isUuid } from "uuid"
import type { Occurrence } from "../interface/occurrences"
import type { Publication } from "../interface/publications"
import { usePreviewPublicationMutation } from "../services/publications/publicationsApi"
import { useLazyGetOccurrenceQuery } from "../services/publications/occurrencesApi"
import { readPostBotError } from "../utils/apiErrors"

export type SavedPreviewPublication = Pick<
  Publication,
  "id" | "name" | "revision" | "templateVersionId"
>
interface PreviewState {
  phase:
    | "idle"
    | "sending"
    | "confirmed"
    | "unknownWithId"
    | "unknownWithoutId"
    | "definiteFailure"
  publication?: SavedPreviewPublication
  occurrence?: Occurrence
  occurrenceId?: string
  error?: unknown
  refreshing?: boolean
  refreshError?: unknown
}
interface Entry {
  state: PreviewState
  listeners: Set<() => void>
}
// Tab memory only, per persisted publication. Retain in-flight/unknown outcomes on
// route departure; no storage, timers, replay or automatic GET/POST. Task17's
// auth/session boundary must call clearPublicationPreviews on logout/user change.
const entries = new Map<string, Entry>()
function entryFor(id: string): Entry {
  let entry = entries.get(id)
  if (!entry) {
    entry = { state: { phase: "idle" }, listeners: new Set() }
    entries.set(id, entry)
  }
  return entry
}
function update(entry: Entry, state: PreviewState) {
  entry.state = state
  entry.listeners.forEach((notify) => notify())
}
export function clearPublicationPreviews() {
  const previous = [...entries.values()]
  entries.clear()
  previous.forEach((entry) => update(entry, { phase: "idle" }))
}

function receiptState(occurrence: Occurrence): PreviewState {
  return {
    phase:
      occurrence.status === "confirmed"
        ? "confirmed"
        : occurrence.status === "failed"
          ? "definiteFailure"
          : "unknownWithId",
    occurrence,
    occurrenceId: occurrence.id,
  }
}
function failureState(error: unknown): PreviewState {
  const parsed = readPostBotError(error)
  if (parsed.code === "DELIVERY_UNKNOWN") {
    const id = [
      parsed.meta?.occurrenceId,
      ...parsed.details.map((detail) => detail.meta?.occurrenceId),
    ].find((id) => typeof id === "string" && isUuid(id))
    return id
      ? { phase: "unknownWithId", occurrenceId: id, error }
      : { phase: "unknownWithoutId", error }
  }
  // Only a proven rejection is a definite failure. Generic network/gateway/
  // parsing errors can hide a completed write; never turn them into safe retries.
  const definite =
    typeof parsed.status === "number" &&
    ((parsed.status >= 400 && parsed.status < 500 && parsed.status !== 408) ||
      (parsed.source === "feature" &&
        (parsed.status === 502 || parsed.status === 503)))
  return { phase: definite ? "definiteFailure" : "unknownWithoutId", error }
}

export function usePublicationPreview(
  id: string,
  { writable, canRead }: { writable: boolean; canRead: boolean },
) {
  const entry = entryFor(id)
  const subscribe = useCallback(
    (notify: () => void) => {
      entry.listeners.add(notify)
      return () => {
        entry.listeners.delete(notify)
      }
    },
    [entry],
  )
  const state = useSyncExternalStore(subscribe, () => entry.state)
  const [post] = usePreviewPublicationMutation()
  const [get] = useLazyGetOccurrenceQuery()
  const current = (attempt: PreviewState) =>
    entries.get(id) === entry && entry.state === attempt
  const send = async (publication: SavedPreviewPublication, pin: boolean) => {
    if (
      !writable ||
      !isUuid(id) ||
      publication.id !== id ||
      entry.state.phase === "sending" ||
      entry.state.refreshing ||
      entries.get(id) !== entry
    )
      return
    const attempt: PreviewState = {
      phase: "sending",
      publication: { ...publication },
    }
    update(entry, attempt) // Synchronous lock also protects multiple mounted actions.
    try {
      const occurrence = await post({
        id,
        ...(pin ? { pin: true } : {}),
      }).unwrap()
      if (
        !occurrence ||
        !isUuid(occurrence.id) ||
        occurrence.purpose !== "preview" ||
        occurrence.publicationId !== id
      )
        throw new Error(
          "Не удалось сопоставить ответ с отправленной публикацией",
        )
      if (current(attempt))
        update(entry, {
          ...receiptState(occurrence),
          publication: attempt.publication,
        })
    } catch (error) {
      if (current(attempt))
        update(entry, {
          ...failureState(error),
          publication: attempt.publication,
        })
    }
  }
  const refresh = async () => {
    const { occurrenceId } = entry.state
    if (
      !canRead ||
      !occurrenceId ||
      entry.state.phase === "sending" ||
      entry.state.refreshing ||
      entries.get(id) !== entry
    )
      return
    const attempt = {
      ...entry.state,
      refreshing: true,
      refreshError: undefined,
    }
    update(entry, attempt)
    try {
      const occurrence = await get(occurrenceId, false).unwrap()
      if (occurrence.id !== occurrenceId)
        throw new Error(
          "Ответ относится к другой отправке; прежний результат сохранён",
        )
      if (current(attempt))
        update(entry, {
          ...receiptState(occurrence),
          publication: attempt.publication,
        })
    } catch (refreshError) {
      if (current(attempt))
        update(entry, { ...attempt, refreshing: false, refreshError })
    }
  }
  return { state, send, refresh }
}
